import {
  LayoutGrid,
  ListFilter,
  Plus,
  List,
  LayoutList,
  UserRoundPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { ProjectUser } from "./types";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";

import { FilterMenu } from "./FilterMenu";
import CreateCanvasModal from "@/components/CreateCanvasModal";
import { KanbanDefaultStates } from "./CanvasKanban";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { ProjectStatus } from "@/components/ProjectConfiguration";
import { getStatusNameFromKey as getStatusNameFromKeyUtil } from "@/components/ProjectConfiguration/utils/statusNameUtils";
import { useCreateCanvasModal } from "@/hooks/useCreateCanvasModal";
import { useShortcuts } from "../providers/ShortcutProvider";
import { CommandPalette } from "./CommandPalette";
import { CommandPaletteOption } from "@/store/zustand/commandPaletteStore";
import { OptionCategory, useCommandPalette } from "@/hooks/useCommandPalette";
import { InviteModal } from "./InviteModal";
import { getAltKey, getModifierKey } from "@/utils/utils";
import { useProjectWithUsers } from "@/hooks/useProjectWithUsers";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { Issue } from "@/types/issues";
import { resolveIssueStatus } from "@/utils/filterUtils";
import { FilterState } from "./types";

interface Props {
  project: any;
  setViewType: Dispatch<SetStateAction<"grid" | "list">>;
  viewType: "grid" | "list";
  showInviteButton?: boolean;
  filters: FilterState;
  updateFilters: (filters: Partial<FilterState>) => void;
}

interface UserFilterProps {
  users: Array<ProjectUser>;
  selectedUsers: Array<string>;
  onChange: (ids: Array<string>) => void;
}

interface FilterMenuHandle {
  clearFilters: () => void;
  openMenu: () => void;
}

export function ProjectPageHeader({
  project,
  setViewType,
  viewType,
  showInviteButton = true,
  filters,
  updateFilters,
}: Props) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const currentProject = useProjectWithUsers();

  const customProjectStatuses = useMemo(() => {
    if (
      !(currentProject as any)?.statuses ||
      !Array.isArray((currentProject as any).statuses)
    )
      return [];
    return (currentProject as any).statuses as ProjectStatus[];
  }, [(currentProject as any)?.statuses]);

  const getStatusNameFromKey = useCallback(
    (key: string | null) => {
      return getStatusNameFromKeyUtil(key, customProjectStatuses);
    },
    [customProjectStatuses],
  );
  const { isOpen, setIsOpen } = useCreateCanvasModal();
  const filterMenuRef = useRef<FilterMenuHandle>(null);
  const { registerShortcut } = useShortcuts();

  // Get canvas previews to calculate createIndex
  const projectIssues = useIssuesStore(
    (state) => state.issuesByProject[project?.id ?? ""],
  );

  // Calculate stateToCanvases
  const stateToCanvases = useMemo(() => {
    if (!project || !projectIssues) return {};

    // Initialize columns with empty canvas arrays
    const stateToCanvases = {};
    Object.keys(KanbanDefaultStates).forEach((key) => {
      stateToCanvases[KanbanDefaultStates[key]] = {
        stateKey: key,
        title: key,
        issues: [],
      };
    });

    // Build available status names from default and custom statuses
    const availableStatusNames = [
      ...Object.values(KanbanDefaultStates),
      ...customProjectStatuses.map((status) => status.name),
    ];

    // Group canvases into their respective columns
    Object.values(projectIssues).forEach((issue: Issue) => {
      const resolvedStatus = resolveIssueStatus(
        issue?.featurePrd?.status,
        availableStatusNames,
      );

      const targetColumn =
        stateToCanvases[resolvedStatus] ||
        stateToCanvases[KanbanDefaultStates.BACKLOG];

      if (targetColumn) {
        targetColumn.issues.push(issue);
      }
    });

    return stateToCanvases;
  }, [project, projectIssues, customProjectStatuses]);

  // Command palette options
  const globalCommandPaletteOptions = useMemo<CommandPaletteOption[]>(
    () =>
      [
        {
          icon: <ListFilter className="size-4" />,
          name: "Clear All Filters",
          shortcut: [getAltKey(), "shift", "F"],
          registerKeys: "alt+shift+f",
          action: () => filterMenuRef.current?.clearFilters(),
          hasFollowUp: false,
        },
        {
          icon: <Plus className="size-4" />,
          name: "Create Canvas",
          shortcut: ["C"],
          registerKeys: "c",
          action: () => setIsOpen("BACKLOG"),
          preventDefault: true,
          hasFollowUp: true,
        },
        {
          icon: <ListFilter className="size-4" />,
          name: "Open Filters",
          shortcut: ["F"],
          registerKeys: "f",
          action: () => filterMenuRef.current?.openMenu(),
          preventDefault: true,
          hasFollowUp: true,
        },
        {
          icon:
            viewType === "grid" ? (
              <LayoutList className="size-4" />
            ) : (
              <LayoutGrid className="size-4" />
            ),
          name: `Switch to ${viewType === "grid" ? "List" : "Grid"} View`,
          shortcut: [getModifierKey(), "B"],
          registerKeys: "mod+b",
          action: () => setViewType(viewType === "grid" ? "list" : "grid"),
          preventDefault: true,
          hasFollowUp: false,
        },
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [viewType, setViewType],
  );

  // Register all shortcuts
  useEffect(() => {
    globalCommandPaletteOptions.forEach((option) => {
      registerShortcut(
        option.registerKeys,
        (e) => {
          e.preventDefault();
          option.action?.();
        },
        { preventDefault: option.preventDefault },
      );
    });
  }, [registerShortcut, globalCommandPaletteOptions]);

  const { registerCategory, open, showCategory } = useCommandPalette();

  useEffect(() => {
    registerCategory(OptionCategory.GLOBAL, globalCommandPaletteOptions);
    showCategory(OptionCategory.GLOBAL);
  }, [globalCommandPaletteOptions]);

  // Register command palette shortcut separately since it's not in the options
  useEffect(() => {
    registerShortcut(
      "mod+k",
      (e) => {
        e.preventDefault();
        open();
      },
      { preventDefault: true },
    );
  }, [registerShortcut]);

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!project) {
        return;
      }

      const search = event?.target?.value as string;

      updateFilters({
        search,
      });
    },
    [updateFilters, project],
  );

  const handleFilterMenuOpenChange = useCallback((open: boolean) => {
    if (open) {
      useProjectModalStore.getState().addProjectModal("filter-menu");
    } else {
      useProjectModalStore.getState().removeProjectModal("filter-menu");
    }
  }, []);

  return (
    <div className="flex flex-col gap-4 w-full border-b border-secondary-n-950">
      <div className="flex items-center justify-between border-b border-secondary-n-900 min-w-0 p-2">
        <div className="flex items-center gap-3">
          <div className="w-72 relative">
            <SearchInput
              value={filters?.search || ""}
              onChange={handleSearch}
              onClear={() => updateFilters({ search: "" })}
              placeholder="Search"
              className="pr-5"
              key={project?.id}
            />
          </div>
        </div>

        {/* Right side - Controls */}
        <div className="flex items-center gap-2 ml-auto">
          <FilterMenu
            ref={filterMenuRef}
            className="rounded-md"
            onOpenChange={handleFilterMenuOpenChange}
            filters={filters}
            updateFilters={updateFilters}
          />
          <div className="flex items-center h-7 gap-0.5 ring-inset ring-secondary-n-800 bg-secondary-n-950 rounded-md p-1 py-2">
            <Button
              onClick={() => setViewType("grid")}
              className={cn(
                "flex items-center px-1.5 hover:bg-background-tertiary transition-colors rounded-md",
                viewType === "grid" && "bg-background-tertiary text-white",
              )}
              variant="ghost"
              size="xs"
            >
              <LayoutGrid className="size-4" />
            </Button>
            <Button
              onClick={() => setViewType("list")}
              className={cn(
                "flex items-center px-1.5 hover:bg-background-tertiary transition-colors rounded-md",
                viewType === "list" && "bg-background-tertiary text-white",
              )}
              variant="ghost"
              size="xs"
            >
              <List className="size-4" />
            </Button>
          </div>
          {showInviteButton && (
            <>
              <div className="border-r h-7 border-secondary-n-900">{""}</div>
              <Button
                onClick={() => setShowInviteModal(true)}
                className="h-7 flex items-center gap-1"
                variant="secondary"
              >
                <UserRoundPlus className="size-4" />
                <p className="text-xs">Invite</p>
              </Button>
            </>
          )}
        </div>
        {showInviteButton && (
          <InviteModal
            open={showInviteModal}
            setOpen={setShowInviteModal}
            projectId={project?.id}
          />
        )}
        <CommandPalette />
        <CreateCanvasModal
          openCanvasState={getStatusNameFromKey(isOpen)}
          setOpenCanvasState={(state: string | null) => {
            setIsOpen(null);
            useProjectModalStore
              .getState()
              .removeProjectModal("create-canvas-modal");
          }}
          project={currentProject}
          createIndex={
            typeof isOpen === "string" && KanbanDefaultStates[isOpen]
              ? stateToCanvases?.[KanbanDefaultStates[isOpen]]?.issues
                  ?.length || 0
              : 0
          }
          filters={filters}
        />
      </div>
    </div>
  );
}
